import { Button, Divider, Grid, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { GetTrainingModelDetails, handleToggleTrainingModelDetailsDialog } from '../../store/mlcreator-slice/mlcreator';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { FiberManualRecordOutlined } from '@mui/icons-material';
import Transition from '../common/Transition';
import TrainingDetails from './TrainingDetails';
import { Header } from '../common/Header';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const TrainingModelDetails = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        trainingModelDetailsLoading,
        trainingModelDetails,
        trainingModelDialogBodyProps,
        trainingModelDetailsDialogOpen
    } = useAppSelector((state: RootState) => state.mlcreator);

    function handleClose() {
        dispatch(handleToggleTrainingModelDetailsDialog({ isOpen: false, body: null }))
    }

    useEffect(() => {
        if (trainingModelDetailsDialogOpen)
            dispatch(GetTrainingModelDetails(trainingModelDialogBodyProps.id));
    }, [trainingModelDetailsDialogOpen])

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={trainingModelDetailsDialogOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header
                title={t('TRAINING_MODEL_TITLE', { name: trainingModelDialogBodyProps?.name })}
                helperIcon={
                    <RedirectIconButton to={'/help/3'} className="helper-icon" >
                        <ContactSupportIcon />
                    </RedirectIconButton>
                }
            />
            <DialogContent>
                {!trainingModelDetails || trainingModelDetailsLoading ?
                    <Grid container p={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {trainingModelDetailsLoading ?
                            <Box sx={{ width: '100%' }}>
                                <LinearProgress />
                            </Box>
                            : !trainingModelDetails ?
                                <Typography variant={'h6'}>{t('NO_TRAINING_MODEL_DETAILS')}</Typography>
                                : ''}
                    </Grid> :
                    <React.Fragment>
                        <Grid container sx={{ display: 'flex', flexDirection: 'row' }} spacing={10}>
                            <Grid item xs={6}>
                                <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <HelpOutlineIcon />
                                    <Typography fontWeight="bold" ml={1} variant='h6'>{t('TRAINING_MODEL_DETAILS_REQUEST')}</Typography>
                                </Grid>
                                <Grid item mt={1.5} xs={12}><Divider /></Grid>
                                <Grid mb={1} mt={1.5} item xs={12}>
                                    <Grid container display="flex" justifyContent="space-between">
                                        <Grid item fontWeight="bold">
                                            {t('TRAINING_MODEL_DETAILS_MODEL_TYPE_NAME')}
                                        </Grid>
                                        <Grid item>
                                            {trainingModelDetails?.request?.modelTypeString
                                                ? t(`${trainingModelDetails?.request?.modelTypeString}`)
                                                : '---'}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                { trainingModelDetails?.request?.algorithms?.length > 0 ? <Divider /> : '' }
                                <Grid mt={1.5} item xs={12}>
                                    {trainingModelDetails?.request?.algorithms?.length > 0 && (
                                        <Grid container mt={1}>
                                            <Grid item xs={12} fontWeight="bold" mb={0.5}>
                                                {t('TRAINING_MODEL_DETAILS_ALGORITHMS')}
                                            </Grid>
                                            {trainingModelDetails.request.algorithms.map((algo: { algorithmName: string; executionTime: number }, index: number) => (
                                                <Grid
                                                    key={index}
                                                    container
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    sx={{ pl: 1, pr: 1, mb: 0.5 }}
                                                >
                                                    <Grid item>{t(algo.algorithmName)}</Grid>
                                                    <Grid item>{algo.executionTime} s</Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item mt={1.5} xs={12}><Divider /></Grid>
                                <Grid container mt={1.5} display={"flex"} flexDirection={"column"}>
                                    <Grid item xs={12}>
                                        <Grid item xs={12} fontWeight="bold">
                                            {t('TRAINING_MODEL_DETAILS_INPUT_COLUMNS')}
                                        </Grid>
                                        <Grid item xs={12} sx={{ height: '199px', overflowY: 'auto' }}>
                                            {trainingModelDetails?.request?.inputColumns?.map((el: string, index: number) => {
                                                return (
                                                    <Grid key={index} item xs={12} pr={1}>
                                                        <Grid container display={"flex"} justifyContent={"space-between"}>
                                                            <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                                                <Typography>{index + 1}.&nbsp;{el}</Typography>
                                                            </Grid>
                                                            {/* <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                            <FiberManualRecordOutlined fontSize='small' />
                                                            <Typography ml={.5}>{el}</Typography>
                                                        </Grid> */}
                                                        </Grid>
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} mt={1.5} mb={2}><Divider /></Grid>
                                    <Grid item xs={12}>
                                        <Grid item xs={12} fontWeight="bold">
                                            {t('TRAINING_MODEL_DETAILS_OUTPUT_COLUMNS')}
                                        </Grid>
                                        <Grid item xs={12} sx={{ height: '30px', overflowY: 'auto' }}>
                                            {trainingModelDetails?.request?.outputColumns?.map((el: string, index: number) => {
                                                return (
                                                    <Grid key={index} item xs={12} pr={1}>
                                                        <Grid container display={"flex"} justifyContent={"space-between"}>
                                                            <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                                                <FiberManualRecordOutlined fontSize='small' />
                                                                <Typography ml={.5}>{el}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            })}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <QuestionAnswerIcon />
                                    <Typography fontWeight="bold" ml={1} variant='h6'>{t('TRAINING_MODEL_DETAILS_RESPONSE')}</Typography>
                                </Grid>
                                <Divider />
                                <TrainingDetails trainingDetails={trainingModelDetails?.response} accuracyPlotDataJson={trainingModelDetails?.accuracyPlotDataJson} />
                            </Grid>
                        </Grid>
                    </React.Fragment>
                }
            </DialogContent>
            <DialogActions>
                <Button className='button-text-color' onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default TrainingModelDetails;